import { useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Area,
  Brush,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Updater, useImmer } from "use-immer";

import { TimeSeriesColors } from "@intelligems/ig-graphs";
import {
  IgAnalyticsTooltip,
  IgCheckbox,
  IgTypography,
} from "@intelligems/ig-theme";
import { GraphConfig, TimeSeriesData } from "@intelligems/ig-types";
import { hexToRgbA } from "@intelligems/ig-utils";
import { FormControlLabel, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useOrgTimezone } from "@src/api/organizations/get-organization";
import { GraphIntervalButtons } from "@src/features/analytics/baseDashboard/chartComponents/GraphIntervalButtons";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { InteractiveChartLegend } from "@src/features/analytics/legends/InteractiveChartLegend";
import {
  ReferenceLineTooltip,
  TimeSeriesTooltip,
} from "@src/features/analytics/metricDetails/timeSeries/TimeSeriesComponentHelpers";
import { TimeSeriesSkeleton } from "@src/features/analytics/metricDetails/timeSeries/TimeSeriesSkeleton";
import { getReferenceLinesFromExperienceActions } from "@src/features/analytics/utils";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";
import { mpClickAnalyticsInteractiveLegend } from "@src/utils/mixpanel";

import { useAddGraphWatermark } from "../../hooks/use-add-graph-watermark";

interface TimeSeriesGraphProps {
  graphConfigs: GraphConfig[];
  selectedMetricId: string;
  graphInterval: string;
  setGraphInterval: Updater<string>;
  data: TimeSeriesData[];
  cumulative: boolean;
  setCumulative: Updater<boolean>;
  confidenceInterval: boolean;
  setConfidenceInterval: Updater<boolean>;
  isLoading: boolean;
}

export const TimeSeriesGraph = ({
  graphConfigs,
  setGraphInterval,
  graphInterval,
  selectedMetricId,
  data,
  cumulative,
  setCumulative,
  setConfidenceInterval,
  confidenceInterval,
  isLoading,
}: TimeSeriesGraphProps) => {
  const timezone = useOrgTimezone(true) as string;
  const theme = useTheme();
  const selectedConfig = useMemo(() => {
    return (
      graphConfigs.find((c) => c.impactMetric === selectedMetricId) ||
      graphConfigs.find((c) => c.metrics[0] === selectedMetricId) ||
      graphConfigs[0]
    );
  }, [selectedMetricId]);
  const [focusGroups, setFocusGroups] = useImmer<string[]>([]);
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const variations = useSelector(selectedVariations);
  const graphValueFormatter = useGraphValueFormatter();
  const experience = useSelector(selectedExperience);

  const colorMap = variations.reduce((acc, variation, index) => {
    acc[variation.id] = TimeSeriesColors[index];
    return acc;
  }, {});

  const handleLegendClick = (dataKey: string) => {
    if (focusGroups.includes(dataKey)) {
      setFocusGroups(focusGroups.filter((el) => el !== dataKey));
    } else {
      mpClickAnalyticsInteractiveLegend(
        experience.id!,
        dataKey,
        "Timeseries Chart"
      );
      setFocusGroups((prev) => [...prev, dataKey]);
    }
  };

  const fakeLineData = useMemo(() => {
    return [
      "Page A",
      "Page B",
      "Page C",
      "Page D",
      "Page E",
      "Page F",
      "Page G",
    ].map((point) => ({
      name: point,
      ...Object.fromEntries(
        variations.map((variation) => [
          variation.id,
          Math.floor(Math.random() * 3) + 1,
        ])
      ),
    }));
  }, []);

  const hasConfidenceIntervalData = useMemo(() => {
    if (!data[5]?.[`${variations[0].id}-range`]) {
      return false;
    }
    return true;
  }, [data]);

  const gridRef = useRef(null);
  useAddGraphWatermark({
    containerRef: gridRef,
    isDataLoading: isLoading,
    showWatermark: true,
  });

  const hasNoData =
    !isLoading && (!data || data.length < 1 || data.length === 0);

  return (
    <Grid
      container
      direction={"column"}
      ref={gridRef}
      sx={{ marginBottom: "40px", paddingRight: "30px" }}
    >
      <Grid
        container
        direction={"row"}
        alignItems={"center"}
        sx={{ padding: "30px", paddingBottom: "20px" }}
      >
        <Grid sx={{ marginRight: "11px" }}>
          <GraphIntervalButtons
            graphInterval={graphInterval}
            hasNoData={hasNoData}
            isLoading={isLoading}
            setGraphInterval={setGraphInterval}
          />
        </Grid>
        <Grid sx={{ marginRight: "11px", marginLeft: "13px" }}>
          <IgAnalyticsTooltip
            title={
              hasNoData
                ? "Cumulative checkbox is disabled when no data is returned"
                : isLoading
                  ? "Cumulative checkbox is disabled while data is loading"
                  : ""
            }
          >
            <FormControlLabel
              disabled={isLoading || hasNoData}
              sx={{ fontSize: "12px" }}
              control={
                <IgCheckbox
                  disabled={isLoading || hasNoData}
                  name={"cumulative"}
                  checked={cumulative}
                  size={"small"}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      setConfidenceInterval(false);
                    }
                    setCumulative((draft) => {
                      draft = !draft;
                      return draft;
                    });
                  }}
                />
              }
              label={
                <IgTypography
                  sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}
                  variant={"body2"}
                >
                  Cumulative
                </IgTypography>
              }
            />
          </IgAnalyticsTooltip>
        </Grid>
        {cumulative && hasConfidenceIntervalData && (
          <Grid>
            <IgAnalyticsTooltip
              title={
                hasNoData
                  ? "Confidence Interval checkbox is disabled when no data is returned"
                  : isLoading
                    ? "Confidence Interval checkbox disabled while data is loading"
                    : ""
              }
            >
              <FormControlLabel
                disabled={isLoading || hasNoData}
                sx={{ fontSize: "12px" }}
                control={
                  <IgCheckbox
                    name={"confidenceIntervals"}
                    size={"small"}
                    checked={confidenceInterval}
                    onClick={() => {
                      setConfidenceInterval((draft) => {
                        draft = !draft;
                        return draft;
                      });
                    }}
                  />
                }
                label={
                  <IgTypography
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                    }}
                    variant={"body2"}
                  >
                    Confidence Intervals
                  </IgTypography>
                }
              />
            </IgAnalyticsTooltip>
          </Grid>
        )}
      </Grid>
      {hasNoData ? (
        <Grid container direction={"row"} justifyContent={"center"}>
          <IgTypography>
            No Data - Your filters may have resulted in no data, try resetting
            them
          </IgTypography>
        </Grid>
      ) : isLoading ? (
        <TimeSeriesSkeleton />
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart
            data={data}
            margin={{
              top: 15,
              bottom: 5,
              left: 40,
            }}
          >
            <CartesianGrid
              style={{
                stroke: theme.igBorders.core.disabled,
                border: "1px solid red",
              }}
              verticalCoordinatesGenerator={(props) => {
                return [props.width];
              }}
            />
            <XAxis
              domain={["dataMin", "dataMax"]}
              dataKey={"xAxisKey"}
              tickLine={false}
              axisLine={{ stroke: theme.igBorders.core.disabled }}
              style={{
                fontFamily: theme.typography.fontFamily,
                fontSize: 10,
              }}
            />
            <YAxis
              type={"number"}
              domain={["auto", "auto"]}
              axisLine={{ stroke: theme.igBorders.core.disabled }}
              style={{
                fontFamily: theme.typography.fontFamily,
                fontSize: 10,
              }}
              tickLine={false}
              tickFormatter={(value: any) =>
                graphValueFormatter({
                  isAxisTick: true,
                  value,
                  configDataTypes: selectedConfig.dataTypes,
                })!
              }
            />
            <Tooltip
              content={
                <TimeSeriesTooltip
                  config={selectedConfig}
                  colorMap={colorMap}
                  graphInterval={graphInterval}
                />
              }
            />

            {getReferenceLinesFromExperienceActions(
              experience.experienceActions
            ).map((action) => {
              return (
                <ReferenceLine
                  key={action.id}
                  x={action.actionTs / 1000}
                  stroke="rgba(0, 0, 0, 0.23)"
                  strokeDasharray={"5 5"}
                  isFront={true}
                  label={
                    <Label
                      content={(props: any) => {
                        return (
                          <ReferenceLineTooltip
                            props={props}
                            experienceAction={action}
                            timezone={timezone}
                            locale={storeCurrencyConfig!.value.locale}
                          />
                        );
                      }}
                    />
                  }
                />
              );
            })}

            {!confidenceInterval
              ? null
              : variations.map((variation, index) => (
                  <Area
                    activeDot={false}
                    hide={focusGroups.includes(variation.id)}
                    key={`${variation.id}-area`}
                    type="monotone"
                    dataKey={`${variation.id}-interval`}
                    stroke="none"
                    fill={hexToRgbA(TimeSeriesColors[index], 0.3)}
                    connectNulls={false}
                    animationDuration={700}
                  />
                ))}
            {variations.map((variation, index) => (
              <Line
                hide={focusGroups.includes(variation.id)}
                type="monotone"
                key={`${variation.id}-line`}
                dataKey={variation.id}
                stroke={TimeSeriesColors[index]}
                dot={false}
                connectNulls={false}
                animationDuration={700}
                strokeWidth={2}
              />
            ))}
            <Brush
              dataKey="xAxisKey"
              height={30}
              stroke={theme.palette.text.secondary}
            />
            <Legend
              content={
                <InteractiveChartLegend
                  onLegendClick={handleLegendClick}
                  focusGroups={focusGroups}
                  lines={variations.map((variation) => {
                    return { id: variation.id, name: variation.name };
                  })}
                />
              }
              wrapperStyle={{ marginLeft: "40px", paddingTop: "24px" }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Grid>
  );
};
